import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  TextField,
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  CircularProgress,
} from "@mui/material";
import TempleHinduIcon from "@mui/icons-material/TempleHindu";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import axios from "axios";
import image from "./image.png";

const KrishnaPrasadamSearch = () => {
  const [query, setQuery] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const searchBoxRef = useRef(null);

  // Scroll to search bar when results appear
  useEffect(() => {
    if (searched && searchBoxRef.current) {
      searchBoxRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [searched, data]);

  // Fetch results automatically when query changes
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (query.trim()) handleSearch(query);
    }, 600);
    return () => clearTimeout(delayDebounce);
  }, [query]);

  const handleSearch = async (q) => {
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(
        `https://railbackend.vercel.app/search?query=${q}`
      );
      const approved = res.data.filter((item) => item.Status === "Approved");
      setData(approved);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#f9f9f2", minHeight: "100vh" }}>
      {/* Hero Image */}
      <Box
        sx={{
          backgroundImage: `url(${image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: { xs: "45vh", md: "76vh" },
          borderBottomLeftRadius: "40px",
          borderBottomRightRadius: "40px",
          boxShadow: "0px 8px 20px rgba(0,0,0,0.2)",
        }}
      ></Box>

      {/* Title Section */}
      <Container sx={{ textAlign: "center", mt: { xs: 4, md: 6 } }}>
        <Typography
          sx={{
            fontFamily: "'Philosopher', serif",
            color: "#5a381e",
            fontWeight: 700,
            mb: 1,
            fontSize: { xs: "1.5rem", md: "2.8rem" },
          }}
        >
          Krishna Prasadam
        </Typography>
        <Typography
          variant="h6"
          sx={{
            maxWidth: 800,
            mx: "auto",
            color: "#6e523b",
            fontStyle: "italic",
            fontSize: { xs: "1rem", md: "1.2rem" },
          }}
        >
          “mahāprasāde govinde nāma-brahmaṇi vaiṣṇave,
          svalpa-puṇyavatā rājan viśvāso naiva jāyate”
        </Typography>
        <Typography
          sx={{
            mt: 1,
            fontStyle: "italic",
            maxWidth: 700,
            mx: "auto",
            color: "#7b6a4f",
            fontSize: { xs: "0.9rem", md: "1rem" },
          }}
        >
          Those who have little pious credit cannot have faith in Mahāprasādam,
          Lord Govinda, the holy name, or the Vaiṣṇavas.
        </Typography>
      </Container>

      {/* Search Box */}
      <Container
        ref={searchBoxRef}
        maxWidth="sm"
        sx={{
          mt: { xs: 4, md: 8 },
          mb: { xs: 10, md: 14 },
          px: { xs: 2, sm: 4 },
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: 5,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            p: { xs: 3, sm: 4, md: 5 },
            width: "100%",
            maxWidth: 600,
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
              transform: "translateY(-4px)",
            },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: { xs: 2, sm: 3 },
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
              color: "#4a3a2f",
              fontSize: { xs: "1rem", sm: "1.2rem", md: "1.4rem" },
              lineHeight: 1.5,
            }}
          >
            Search for{" "}
            <span style={{ color: "#7B4B00" }}>Krishna Prasadam Seva</span> at
            your Railway Station 🚆
          </Typography>

          <TextField
            variant="outlined"
            label="Enter Station Name"
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                fontSize: { xs: "0.9rem", md: "1rem" },
              },
              "& label": { fontSize: { xs: "0.85rem", md: "1rem" } },
              "& label.Mui-focused": { color: "#7B4B00" },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#c7a77b",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#7B4B00",
              },
              transition: "all 0.3s ease-in-out",
            }}
          />
        </Box>
      </Container>

      {/* Loader */}
      {loading && (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress size={60} thickness={5} />
          <Typography sx={{ mt: 2, color: "#777" }}>
            Fetching Prasadam seva details...
          </Typography>
        </Box>
      )}

      {/* Results Section */}
      {!loading && searched && data.length > 0 && (
        <Container sx={{ mt: { xs: 1, md: 2 }, pb: 8 }}>
          <Grid
            container
            spacing={3}
            justifyContent="center"
            alignItems="stretch"
            sx={{
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            {data.map((item) => (
              <Grid
                item
                key={item._id}
                xs={12}
                sm={6}
                md={4}
                lg={3}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "stretch",
                  flex: "0 1 300px",
                  maxWidth: "300px",
                }}
              >
                <Card
                  sx={{
                    flex: 1,
                    borderRadius: 4,
                    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                    background:
                      "linear-gradient(145deg, #fffdf5 0%, #f6f6ef 100%)",
                    transition: "transform 0.25s ease, box-shadow 0.25s ease",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    width: "100%",
                    minHeight: 240,
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      p: 3,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                      <TempleHinduIcon
                        sx={{ color: "#8b5e34", fontSize: 26, mr: 1 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#3e2723",
                          fontWeight: 600,
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: { xs: "1rem", md: "1.05rem" },
                          lineHeight: 1.3,
                        }}
                      >
                        {item.Name}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        mb: 0.8,
                        fontSize: "0.95rem",
                        color: "#4e342e",
                      }}
                    >
                      <strong>Station:</strong> {item.StationName}
                    </Typography>
                    <Typography
                      sx={{
                        mb: 1,
                        fontSize: "0.95rem",
                        color: "#4e342e",
                      }}
                    >
                      <strong>State:</strong> {item.State}
                    </Typography>
                  </CardContent>

                  {/* Action Icons */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "right",
                      gap: 3,
                      pb: 2,
                      pr: 2,
                    }}
                  >
                    <IconButton
                      onClick={() => window.open(`tel:${item.PhoneNo}`, "_self")}
                      sx={{
                        backgroundColor: "#fff",
                        border: "2px solid #1976d2",
                        color: "#1976d2",
                        "&:hover": {
                          backgroundColor: "#1976d2",
                          color: "#fff",
                        },
                      }}
                    >
                      <PhoneIcon />
                    </IconButton>

                    <IconButton
                      onClick={() =>
                        window.open(
                          `https://wa.me/${item.WANo}?text=Hare Krishna! 🙏 I want Krishna Prasadam at ${item.StationName}.`,
                          "_blank"
                        )
                      }
                      sx={{
                        backgroundColor: "#fff",
                        border: "2px solid #25D366",
                        color: "#25D366",
                        "&:hover": {
                          backgroundColor: "#25D366",
                          color: "#fff",
                        },
                      }}
                    >
                      <WhatsAppIcon />
                    </IconButton>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* No Results */}
      {!loading && searched && data.length === 0 && (
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Typography variant="h6" color="text.secondary">
            🙏 No approved Prasadam seva found for this station. Please try
            another. 🙏
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default KrishnaPrasadamSearch;
